import { Fragment, type JSX, useCallback } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';

import { CodeSpan } from '../../CodeSpan';
import type { MeasureType, Metric } from '../../types';

import styles from './RandomizationUnitsSection.module.css';

interface RandomizationUnitsSectionProps {
  measureType: MeasureType;
  metricKind: Metric['kind'];
  unitAggregationType: Metric['unitAggregationType'];
  formElement?: JSX.Element;
  readonly?: boolean;
  readonlyValues?: string[];
  /** When true, omits the trailing ", where" connector intended to precede success criteria. Defaults to false. */
  omitWhereConnector?: boolean;
}

const RandomizationUnits = ({
  readonlyValues,
}: {
  readonlyValues: NonNullable<RandomizationUnitsSectionProps['readonlyValues']>;
}) => {
  const findPrefix = useCallback(
    (index: number) => {
      if (readonlyValues.length > 2) {
        // use the formatting: "X, Y, and Z"
        return index === 0 ? (
          ''
        ) : index === readonlyValues.length - 1 ? (
          <span className={styles.orText}>or</span>
        ) : (
          ', '
        );
      } else if (readonlyValues.length === 2) {
        // use the formatting: "X or Y"
        return index === readonlyValues.length - 1 && <span className={styles.orText}>or</span>;
      }
    },
    [readonlyValues],
  );

  if (readonlyValues.length === 0) {
    return null;
  }

  if (readonlyValues.length >= 2) {
    return (
      <>
        {readonlyValues.map((rov, index) => {
          const isOrCase = index > 0 && index === readonlyValues.length - 1;
          return (
            <Fragment key={rov}>
              {isOrCase && ' '}
              <span className={styles.commaSpan}>
                {findPrefix(index)}
                <CodeSpan textValue={rov} />
              </span>
            </Fragment>
          );
        })}
      </>
    );
  }

  // use the formatting: "X"
  return <CodeSpan textValue={readonlyValues[0]} />;
};

export const RandomizationUnitsSection: React.FC<RandomizationUnitsSectionProps> = ({
  measureType,
  metricKind,
  unitAggregationType,
  readonlyValues,
  formElement,
  readonly = false,
  omitWhereConnector = false,
}: RandomizationUnitsSectionProps) => (
  <>
    {getFirstCopySection(measureType, metricKind)}
    {readonly && Array.isArray(readonlyValues) ? <RandomizationUnits readonlyValues={readonlyValues} /> : formElement}
    {getSecondCopySection(measureType, metricKind, unitAggregationType, omitWhereConnector)}
  </>
);
const getFirstCopySection = (selectedMeasureType: MeasureType, metricKind: Metric['kind']) => {
  if (selectedMeasureType === 'occurrence') {
    return (
      <span>
        <Message id="metrics.definitionSummary.percentageOf" />{' '}
      </span>
    );
  }
  switch (metricKind) {
    case 'custom':
    case 'trace':
      if (selectedMeasureType === 'value') {
        return (
          <span>
            <Message
              id="metrics.definitionSummary.valueOfThePer"
              values={{ code: (chunks) => <CodeSpan textValue={chunks.join('')} /> }}
            />
          </span>
        );
      } else if (selectedMeasureType === 'count') {
        return (
          <span>
            <Message
              id="metrics.definitionSummary.ofCountPer"
              values={{ kind: metricKind, code: (chunks) => <CodeSpan textValue={chunks.join('')} /> }}
            />
          </span>
        );
      }
      break;
    case 'click':
      return msg('metrics.definitionSummary.clicksPer');
    case 'pageview':
      return msg('metrics.definitionSummary.pageViewsPer');
    default:
      return null;
  }
};

const getSecondCopySection = (
  selectedMeasureType: MeasureType,
  metricKind: Metric['kind'],
  unitAggregationType: Metric['unitAggregationType'],
  omitWhereConnector: boolean,
) => {
  if (selectedMeasureType === 'count') {
    return omitWhereConnector ? null : msg('metrics.definitionSummary.whereConnector');
  }
  switch (metricKind) {
    case 'custom':
    case 'trace':
      if (selectedMeasureType === 'value') {
        return (
          <span>
            {' '}
            {msg('metrics.definitionSummary.measureUnit', { kind: metricKind })}{' '}
            <span className={styles.commaSpan}>
              <CodeSpan textValue={unitAggregationType?.toString() ?? ''} />
              {omitWhereConnector ? null : ','}
            </span>
            {omitWhereConnector ? null : msg('metrics.definitionSummary.whereConnector')}
          </span>
        );
      } else if (selectedMeasureType === 'occurrence') {
        return msg('metrics.definitionSummary.unitsThatSent', {
          kind: metricKind,
          where: omitWhereConnector ? 'false' : 'true',
        });
      }
      break;
    case 'click':
      return msg('metrics.definitionSummary.unitsThatClicked', { where: omitWhereConnector ? 'false' : 'true' });
    case 'pageview':
      return msg('metrics.definitionSummary.unitsThatSawPage', { where: omitWhereConnector ? 'false' : 'true' });
    default:
      break;
  }
};
